<script lang="ts" setup>
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { Routes } from '~/enums/Routes'
import { useLang } from '~/composables/useLang'

const { backgroundColor, prefix, faq } = defineProps<{
  faq: { question: TranslatableString; answer: TranslatableString }[]
  prefix?: string
  backgroundColor?: string
}>()
const itemPrefix = prefix ?? 'faq'

const { t } = useLang()
const route = useRoute()

const hash = computed(() => route.hash.split('#')?.[1])
const open = ref()

const scrollToFaqItem = (itemHash: string | undefined) => {
  if (typeof window !== 'undefined' && itemHash?.includes(itemPrefix)) {
    document.getElementsByName(itemHash)?.[0]?.scrollIntoView({
      block: 'center',
    })

    open.value = itemHash
  }
}

onMounted(() => {
  scrollToFaqItem(hash.value)
})

watch(hash, (newHash) => {
  scrollToFaqItem(newHash)
})

const items = computed(() =>
  faq.map((item, index) => ({
    label: item.question,
    content: item.answer,
    defaultOpen: hash.value === `${itemPrefix}${index + 1}`,
  }))
)
</script>

<template>
  <WidgetLayout v-if="Boolean(faq?.length)" :background-color="backgroundColor ?? '#F3F3F3'" class="faq-component">
    <div class="faq">
      <h2 class="h5 faq-heading">{{ t('faq.title') }}</h2>
      <hr />
      <UAccordion
        :items="items"
        :ui="{
          container: 'faq-item',
          wrapper: 'gap-4',
          item: {
            padding: 'px-5',
          },
        }"
        multiple>
        <template #default="{ item, index, open }">
          <div
            class="faq-item-header flex w-full items-center"
            :class="[open && '!text-brand-primary']"
            :name="`${itemPrefix}${index + 1}`">
            {{ getWidgetTextTranslation(item.label) }}
            <div class="ml-auto">
              <i
                class="transform transition-transform duration-200 fas fa-chevron-down"
                :class="[open && 'rotate-180']" />
            </div>
          </div>
        </template>

        <template #item="{ item }">
          <div class="faq-body">
            <div class="text-sm" v-html="getWidgetTextTranslation(item.content)" />
          </div>
        </template>
      </UAccordion>

      <div class="faq-page-link">
        <hr />
        <div class="flex justify-center flex-wrap items-center gap-6">
          <span
            class="text-lg text-text1 dark:text-dmText1 font-bold alt:font-medium"
            v-text="t('footer.cant-find-the-answer')" />
          <NuxtLink class="btn-white w-full md:w-min !px-[3.75rem]" :to="getLocalizedRoute(Routes.Faq.Index)">
            {{ t('footer.see-all-faq') }}
          </NuxtLink>
        </div>
      </div>

      <hr />
    </div>
  </WidgetLayout>
</template>

<style lang="postcss" scoped>
.faq-component {
  @apply py-2 p-4 pb-4 md:pt-10 md:pb-5 lg:pb-3;

  .faq {
    hr {
      @apply border-neutral6;
    }

    .faq-heading {
      @apply text-text1 dark:text-dmText1;
    }

    .faq-item ~ .faq-item {
      @apply mt-[1rem];
    }

    :deep(.faq-item) {
      @apply p-0 rounded-md overflow-hidden alt:rounded-sm;
      @apply shadow-none cursor-pointer hover:dark:bg-dmGray1;

      .faq-item-header {
        @apply text-text1 dark:text-dmText1 border-0 bg-transparent p-[1.25rem];

        .icon {
          @apply text-text1 dark:text-dmText1;
        }
      }

      .faq-body {
        @apply text-text1 dark:text-white border-0 bg-transparent p-0;

        .content {
          @apply pl-[1.25rem] pr-[3.25rem] pt-0 pb-[1.25rem] opacity-0;
        }
      }
    }

    :deep(.faq-item[data-headlessui-state='open']) {
      @apply bg-white dark:bg-dmGray2;
    }
  }
}
</style>

<style lang="postcss">
.faq-component {
  @apply p-0;

  .faq {
    .faq-item-header {
      .icon {
        position: relative;
        margin-left: 3rem;
        font-family: theme('vars.fontAwesome');
        font-weight: 900;
        font-style: normal;
        line-height: 1;
        background-color: transparent;
        mask-image: none;

        &:before {
          display: flex;
          align-items: center;
          justify-content: center;
          content: '\f107';
          position: relative;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}
</style>

<style lang="postcss" scoped>
.dark .faq-component {
  @apply !bg-dmGray5;
}
</style>
